import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import { useBookingPermissions } from "../../../../../../hooks/useBookingPermissions";
import { postUpdateBookingV4 } from "../../../../../../APIs/Booking/UpdateBooking";
import { BookingTag } from "../../../../../../types/VenueGroup";
import { BookingV4, CustomFieldTag } from "../../../../../../types/bookings";
import { buildGridLayout } from "../../../../../../utils/gridLayout";
import { CustomFieldCard } from "./CustomFieldCard";
import { CollapsibleSection } from "../../../../../blocks/CollapsibleSection";

import type { CustomFieldValue } from "./CustomFieldCard";

type TagValues = Record<string, CustomFieldValue>;

const DEBOUNCE_DELAY = 300;
const ERROR_TOAST_DURATION = 4000;

const tagsToValues = (tags: CustomFieldTag[]): TagValues => {
  const values: TagValues = {};
  tags.forEach((tag) => {
    values[tag.id] = tag.value ?? null;
  });
  return values;
};

const valuesToTags = (
  values: TagValues,
  fields: BookingTag[],
  existingTags: CustomFieldTag[]
): CustomFieldTag[] => {
  return fields.map((field) => {
    const existingTag = existingTags.find((t) => t.id === field.id);
    const value = values[field.id] ?? null;

    return {
      id: field.id,
      value,
      label: field.label,
      type: field.type,
      ...(existingTag?.text ? { text: existingTag.text } : {})
    };
  });
};

const getFilledCount = (values: TagValues): number => {
  return Object.values(values).filter((v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "boolean") return true;
    if (Array.isArray(v)) return v.length > 0;
    return String(v).trim() !== "";
  }).length;
};

const getSummary = (values: TagValues, fields: BookingTag[]): string => {
  const filledFields = fields.filter((field) => {
    const v = values[field.id];
    if (v === null || v === undefined) return false;
    if (typeof v === "boolean") return v;
    if (Array.isArray(v)) return v.length > 0;
    return String(v).trim() !== "";
  });

  if (filledFields.length === 0) return "No tags set";

  const preview = filledFields.slice(0, 2).map((field) => {
    const v = values[field.id];
    if (typeof v === "boolean") return field.label;
    if (Array.isArray(v)) return `${field.label}: ${v.join(", ")}`;
    return `${field.label}: ${v}`;
  });

  const remaining = filledFields.length - 2;
  return preview.join(" \u00b7 ") + (remaining > 0 ? ` +${remaining}` : "");
};

const CustomFieldsEditable = () => {
  const diaryContext = useDiaryContext();
  const { selectedBooking, selectedBookingV4, setDiaryContext } = diaryContext;
  const { venueGroupConfig } = useAppContext();
  const notificationsContext = useNotificationsContext();
  const { userCanManageBooking: canEdit } = useBookingPermissions();

  const bookingTagIds = useMemo(
    () => new Set((selectedBookingV4?.tags ?? []).map((t) => t.id)),
    [selectedBookingV4?.tags]
  );

  const allTags = venueGroupConfig?.booking_tags ?? [];

  const customFields = useMemo(
    () => allTags.filter((tag: BookingTag) => !tag.archived_at),
    [allTags]
  );

  const archivedFieldsWithValues = useMemo(
    () =>
      allTags.filter(
        (tag: BookingTag) => !!tag.archived_at && bookingTagIds.has(tag.id)
      ),
    [allTags, bookingTagIds]
  );

  const visibleFields = useMemo(
    () => [...customFields, ...archivedFieldsWithValues],
    [customFields, archivedFieldsWithValues]
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousValuesRef = useRef<TagValues>({});
  const localValuesRef = useRef<TagValues>({});
  const selectedBookingV4Ref = useRef(selectedBookingV4);
  const mountedRef = useRef(true);
  const isSavingRef = useRef(false);
  const saveRef = useRef<(v: TagValues) => Promise<void>>();

  const [localValues, setLocalValues] = useState<TagValues>(() =>
    tagsToValues(selectedBookingV4?.tags ?? [])
  );

  useEffect(() => {
    localValuesRef.current = localValues;
  }, [localValues]);

  useEffect(() => {
    selectedBookingV4Ref.current = selectedBookingV4;

    if (isSavingRef.current || debounceTimer.current) {
      return;
    }
    const incomingValues = tagsToValues(selectedBookingV4?.tags ?? []);
    const currentValues = localValuesRef.current;
    const keysMatch =
      Object.keys(incomingValues).length === Object.keys(currentValues).length;
    const valuesMatch =
      keysMatch &&
      Object.entries(incomingValues).every(([key, val]) => {
        const cur = currentValues[key];
        if (Array.isArray(val) && Array.isArray(cur)) {
          return val.length === cur.length && val.every((v, i) => v === cur[i]);
        }
        return val === cur;
      });
    if (!valuesMatch) {
      setLocalValues(incomingValues);
    }
  }, [selectedBookingV4]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
        saveRef.current?.(localValuesRef.current);
      }
    };
  }, []);

  const rollbackValues = useCallback(
    (currentBooking: BookingV4) => {
      if (!mountedRef.current) return;
      const previousTags = valuesToTags(
        previousValuesRef.current,
        customFields,
        currentBooking.tags ?? []
      );
      setLocalValues(previousValuesRef.current);
      setDiaryContext({
        selectedBookingV4: {
          ...currentBooking,
          tags: previousTags
        }
      });
      notificationsContext.addNotification({
        heading: "Failed to update custom fields",
        autoClose: ERROR_TOAST_DURATION,
        toastType: "error"
      });
    },
    [setDiaryContext, notificationsContext, customFields]
  );

  const saveCustomFields = useCallback(
    async (updatedValues: TagValues) => {
      const currentBooking = selectedBookingV4Ref.current;
      if (!currentBooking?._id) {
        return;
      }

      isSavingRef.current = true;

      const updatedTags = valuesToTags(
        updatedValues,
        customFields,
        currentBooking.tags ?? []
      );

      const bookingPayload = {
        _id: currentBooking._id,
        tags: updatedTags
      };

      try {
        const response = await postUpdateBookingV4(bookingPayload as BookingV4);

        if (!mountedRef.current) return;

        if (!response) {
          rollbackValues(currentBooking);
          return;
        }

        setDiaryContext({
          selectedBookingV4: {
            ...currentBooking,
            ...response,
            tags: response.tags ?? updatedTags
          }
        });
      } catch {
        if (!mountedRef.current) return;
        rollbackValues(currentBooking);
      } finally {
        isSavingRef.current = false;
      }
    },
    [setDiaryContext, rollbackValues, customFields]
  );

  useEffect(() => {
    saveRef.current = saveCustomFields;
  }, [saveCustomFields]);

  const handleFieldChange = useCallback(
    (fieldId: string, value: CustomFieldValue) => {
      if (!selectedBookingV4Ref.current) {
        return;
      }

      if (!debounceTimer.current) {
        previousValuesRef.current = { ...localValuesRef.current };
      }

      const updatedValues = {
        ...localValuesRef.current,
        [fieldId]: value
      };

      setLocalValues(updatedValues);

      const updatedTags = valuesToTags(
        updatedValues,
        customFields,
        selectedBookingV4Ref.current.tags ?? []
      );

      setDiaryContext({
        selectedBookingV4: {
          ...selectedBookingV4Ref.current,
          tags: updatedTags
        }
      });

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        saveCustomFields(updatedValues);
      }, DEBOUNCE_DELAY);
    },
    [setDiaryContext, saveCustomFields, customFields]
  );

  const filledCount = useMemo(() => getFilledCount(localValues), [localValues]);
  const summary = useMemo(
    () => getSummary(localValues, visibleFields),
    [localValues, visibleFields]
  );

  const gridItems = useMemo(
    () => buildGridLayout(visibleFields, 4),
    [visibleFields]
  );

  if (selectedBooking && !selectedBookingV4) {
    return null;
  }

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      title="Custom Fields"
      badge={String(filledCount)}
      summary={summary}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {gridItems.flatMap((row) =>
          row.map((item) => {
            const colSpanMap: Record<number, string> = {
              4: "sm:col-span-2 md:col-span-4",
              3: "sm:col-span-2 md:col-span-3",
              2: "sm:col-span-2 md:col-span-2",
              1: "sm:col-span-1 md:col-span-1"
            };
            const spanClass =
              colSpanMap[item.colSpan] ?? "sm:col-span-1 md:col-span-1";

            if (item.isCheckboxGroup) {
              return (
                <div
                  key={item.fields.map((f) => f.id).join("-")}
                  className={`flex flex-col gap-3 ${spanClass}`}
                >
                  {item.fields.map((field) => {
                    const isArchived = !!field.archived_at;
                    return (
                      <div
                        key={field.id}
                        className="border border-border rounded-lg p-3"
                      >
                        <CustomFieldCard
                          field={field}
                          value={localValues[field.id] ?? null}
                          canEdit={canEdit && !isArchived}
                          isArchived={isArchived}
                          onChange={handleFieldChange}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }

            const field = item.fields[0];
            const isArchived = !!field.archived_at;
            return (
              <div
                key={field.id}
                className={`border border-border rounded-lg p-3 ${spanClass}`}
              >
                <CustomFieldCard
                  field={field}
                  value={localValues[field.id] ?? null}
                  canEdit={canEdit && !isArchived}
                  isArchived={isArchived}
                  onChange={handleFieldChange}
                />
              </div>
            );
          })
        )}
      </div>
    </CollapsibleSection>
  );
};

export default CustomFieldsEditable;
